import type { Store } from '@blocksuite/affine/store';
import { ZipTransformer } from '@blocksuite/affine/widgets/linked-doc';

import type { WorkspaceService } from '../../workspace';
import { getStreamLainWorkspaceSchema } from '../../workspace/global-schema';
import {
  type ExportScopePlan,
  prepareDatabaseSubdocsForSnapshotExport,
  type SnapshotExportMode,
} from './database-subdoc-preload';

/** A page to export has a part that cannot be loaded on this device. */
export class ExportPageIncompleteError extends Error {
  constructor(cause: unknown) {
    super('A page to export is not fully available on this device.', {
      cause,
    });
    this.name = 'ExportPageIncompleteError';
  }
}

/** The message of a failed export: an incomplete page names its cause. */
export function snapshotExportErrorMessageKey(
  error: unknown
):
  | 'com.streamlain.export.error.pageIncomplete'
  | 'com.streamlain.export.error.message' {
  return error instanceof ExportPageIncompleteError
    ? 'com.streamlain.export.error.pageIncomplete'
    : 'com.streamlain.export.error.message';
}

/**
 * Loads every part of `pages` and keeps them until the returned release, so
 * an export never writes half a page. Rejects with ExportPageIncompleteError.
 */
export async function retainWholePagesForExport(
  pages: readonly Store[],
  signal?: AbortSignal
): Promise<() => void> {
  const releases: (() => void)[] = [];
  const release = () => releases.splice(0).forEach(done => done());
  try {
    for (const page of pages)
      releases.push(await page.pageStructure.retainWholePage(signal));
  } catch (error) {
    release();
    throw new ExportPageIncompleteError(error);
  }
  return release;
}

export type SnapshotExportScopeResolution = {
  plan: ExportScopePlan;
  stores: Store[];
};

export type ResolveSnapshotExportScopeInput = {
  docs: readonly Store[];
  workspaceService: WorkspaceService;
  mode: SnapshotExportMode;
};

export async function resolveSnapshotExportScope(
  input: ResolveSnapshotExportScopeInput
): Promise<SnapshotExportScopeResolution> {
  // The plan reads the database blocks of every part of the pages.
  const release = await retainWholePagesForExport(input.docs);
  try {
    const plan = await prepareDatabaseSubdocsForSnapshotExport(
      input.docs,
      input.workspaceService,
      { mode: input.mode }
    );
    return {
      plan,
      stores: resolveStoresForExportScopePlan(input.docs, plan),
    };
  } finally {
    release();
  }
}

export async function exportDocsWithSnapshotExportScope(input: {
  collection: Store['workspace'];
  docs: readonly Store[];
  workspaceService: WorkspaceService;
  mode: SnapshotExportMode;
  excludeBlobIds?: Set<string>;
  resolvedScope?: SnapshotExportScopeResolution;
}): Promise<ExportScopePlan> {
  const resolvedScope =
    input.resolvedScope ??
    (await resolveSnapshotExportScope({
      docs: input.docs,
      workspaceService: input.workspaceService,
      mode: input.mode,
    }));

  const release = await retainWholePagesForExport(input.docs);
  try {
    await ZipTransformer.exportDocs(
      input.collection,
      getStreamLainWorkspaceSchema(),
      resolvedScope.stores,
      input.excludeBlobIds
    );
  } finally {
    release();
  }

  return resolvedScope.plan;
}

function resolveStoresForExportScopePlan(
  sourceStores: readonly Store[],
  plan: ExportScopePlan
): Store[] {
  const workspace = sourceStores[0]?.workspace;
  const byId = new Map<string, Store>();
  for (const store of sourceStores) {
    byId.set(store.id, store);
  }
  if (!workspace) {
    return [...byId.values()];
  }

  for (const docId of plan.requiredDocIds) {
    if (byId.has(docId)) {
      continue;
    }
    const doc = workspace.getDoc(docId);
    const store = doc?.getStore({ id: docId });
    if (store) {
      byId.set(docId, store);
    }
  }

  return [...byId.values()];
}
